window.debounce = function (fn, wait) {
  let t;
  return function (...a) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, a), wait);
  };
};

/* ── Collection map helper ──
   Fetches ga-gift-collection-data section via Section Rendering API
   Returns { productId: [collectionHandle, ...], ... } */
window._gaFetchCollectionMap = async function () {
  try {
    const res = await fetch('/cart?sections=ga-gift-collection-data');
    const sections = await res.json();
    const html = sections['ga-gift-collection-data'] || '';
    const match = html.match(/<script[^>]*data-ga-collection-map[^>]*>([\s\S]*?)<\/script>/);
    if (match) {
      const raw = JSON.parse(match[1]);
      const map = {};
      for (const [pid, handles] of Object.entries(raw)) {
        map[Number(pid)] = handles;
      }
      return map;
    }
  } catch (e) {
    console.error('[GA] collection map fetch error', e);
  }
  return {};
};

window._gaIsInCollection = function (map, productId, collectionHandle) {
  const handles = map[productId] || [];
  return handles.includes(collectionHandle);
};

function withAlpine(cb) {
  if (window.Alpine) cb(window.Alpine);
  else document.addEventListener('alpine:init', () => cb(window.Alpine));
}

withAlpine((Alpine) => {
Alpine.data('gaCartDrawer', (threshold) => ({
  threshold,
  open: false,
  updating: false,
  updatingKey: null,

  cart: { items: [], total_price: 0, item_count: 0 },
  t: window.GA_CART_TRANSLATIONS || {},
  _collectionMap: {},

    thresholdCents: 0,
    giftThresholdCents: 0,
    nextDayThresholdCents: 0,
    progressMaxCents: 0,
    giftAnimProgress: 0,

    milestoneEnabled: false,
    allowMultiTierGifts: false,
    giftAutoAdd: false,
    m1ThresholdCents: 0,
    m2ThresholdCents: 0,
    giftWorthCents: 0,
    m1WorthCents: 0,
    m2WorthCents: 0,

    close() {
      this.open = false;
    },

    isFreeGift(item) {
      if (item?.properties?._free_gift === 'true') return true;
      if (item?.properties?._free_gift_m1 === 'true') return true;
      if (item?.properties?._free_gift_m2 === 'true') return true;
      return false;
    },

    isMilestoneGift(item, mi) {
      const attr = mi === 0 ? '_free_gift_m1' : '_free_gift_m2';
      return item?.properties?.[attr] === 'true';
    },

    get qualifyingSubtotalCents() {
      return (this.cart.items || []).reduce((sum, item) => {
        if (this.isFreeGift(item)) return sum;
        return sum + (item.final_line_price || 0);
      }, 0);
    },

    get giftQualified() {
      if (this.milestoneEnabled) return false;
      return !!this.giftThresholdCents && this.qualifyingSubtotalCents >= this.giftThresholdCents;
    },

    get hasFreeGift() {
      if (this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isFreeGift(item));
    },

    get m1Qualified() {
      return this.milestoneEnabled && !!this.m1ThresholdCents && this.qualifyingSubtotalCents >= this.m1ThresholdCents;
    },

    get m2Qualified() {
      return this.milestoneEnabled && !!this.m2ThresholdCents && this.qualifyingSubtotalCents >= this.m2ThresholdCents;
    },

    get hasAnyMilestoneGift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 0) || this.isMilestoneGift(item, 1));
    },

    get hasM1Gift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 0));
    },

    get hasM2Gift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 1));
    },

    get remainingToM1Cents() {
      return Math.max(this.m1ThresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get remainingToM2Cents() {
      return Math.max(this.m2ThresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get m1Position() {
      if (!this.progressMaxCents) return 0;
      return Math.min((this.m1ThresholdCents / this.progressMaxCents) * 100, 100);
    },

    get m2Position() {
      if (!this.progressMaxCents) return 0;
      return Math.min((this.m2ThresholdCents / this.progressMaxCents) * 100, 100);
    },

    get remainingToFreeShipCents() {
      return Math.max(this.thresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get remainingToGiftCents() {
      return Math.max(this.giftThresholdCents - this.qualifyingSubtotalCents, 0);
    },
    get remainingToNextDayCents() {
      return Math.max(this.nextDayThresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get nextDayQualified() {
      return !!this.nextDayThresholdCents && this.qualifyingSubtotalCents >= this.nextDayThresholdCents;
    },

    get progressPercent() {
      if (!this.progressMaxCents) return 0;

      return Math.min(
        (this.qualifyingSubtotalCents / this.progressMaxCents) * 100,
        100
      );
    },

    get shippingPosition() {
      if (!this.progressMaxCents) return 0;

      return Math.min(
        (this.thresholdCents / this.progressMaxCents) * 100,
        100
      );
    },

    get giftPosition() {
      if (!this.progressMaxCents) return 0;

      return Math.min(
        (this.giftThresholdCents / this.progressMaxCents) * 100,
        100
      );
    },
    get nextDayPosition() {
        if (!this.progressMaxCents) return 0;

        return Math.min(
          (this.nextDayThresholdCents / this.progressMaxCents) * 100,
          100
        );
      },

    get subtotal() {
      return this.cart.total_price / 100;
    },

    formatMoney(cents) {
      if (window.formatMoneyCents) return window.formatMoneyCents(cents);
      return (cents / 100).toFixed(2);
    },

    init() {
        this.thresholdCents = Math.round((Number(this.threshold) || 0) * 100);
        this.giftThresholdCents = Math.round(
          (Number(window.GIFT_ENGINE_CONFIG?.threshold) || 0) * 100
        );
        this.nextDayThresholdCents = Math.round(
          (Number(window.GA_NEXT_DAY_THRESHOLD) || 0) * 100
        );

        this.milestoneEnabled = !!window.GIFT_ENGINE_CONFIG?.milestoneEnabled;
        this.allowMultiTierGifts = !!window.GIFT_ENGINE_CONFIG?.allowMultiTierGifts;
        this.giftAutoAdd = !!window.GIFT_ENGINE_CONFIG?.giftAutoAdd;
        const milestones = window.GIFT_ENGINE_CONFIG?.milestones || [];
        this.m1ThresholdCents = Math.round((Number(milestones[0]?.threshold) || 0) * 100);
        this.m2ThresholdCents = Math.round((Number(milestones[1]?.threshold) || 0) * 100);
        this.giftWorthCents = Number(window.GIFT_ENGINE_CONFIG?.giftWorthCents) || 0;
        this.m1WorthCents = Number(milestones[0]?.worthCents) || 0;
        this.m2WorthCents = Number(milestones[1]?.worthCents) || 0;

      this.progressMaxCents = Math.max(
        this.thresholdCents,
        this.milestoneEnabled ? this.m1ThresholdCents : 0,
        this.milestoneEnabled ? this.m2ThresholdCents : 0,
        this.milestoneEnabled ? 0 : this.giftThresholdCents,
        this.nextDayThresholdCents
      );

      this.fetchCart();

      window.addEventListener('toggle-cart', () => {
        this.fetchCart();
        this.open = true;
      });

      window.addEventListener('cart:refresh', () => {
        this.fetchCart();
      });
    },

    async fetchCart() {
      try {
        /* Force Shopify to re-evaluate automatic discounts (Buy X Get Y, etc.)
           by touching a cart attribute before reading /cart.js */
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _dt: Date.now().toString() } })
        }).catch(() => {});

        const [data, collectionMap] = await Promise.all([
          fetch('/cart.js').then(r => r.json()),
          window._gaFetchCollectionMap()
        ]);

        this._collectionMap = collectionMap;

        this.cart.items = (data.items || []).map((item) => ({
          product_id: item.product_id,
          key: item.key,
          id: item.variant_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          variant_title: item.variant_title,
          product_title: item.product_title,
          final_line_price: item.final_line_price,
          original_line_price: item.original_line_price,
          final_unit_price: item.final_price ?? item.price,
          original_unit_price: item.original_price,
          image: item.image,
          url: item.url,
          price: item.price,
          original_price: item.original_price,
          discounts: (item.line_level_discount_allocations || []).map(d => d.discount_application?.title).filter(Boolean),
          properties: item.properties || {}
        }));

        this.cart.total_price = data.total_price || 0;
        this.cart.item_count = data.item_count || 0;

        window.GA_CART = data;
        window.GA_COLLECTION_MAP = collectionMap;

        window.dispatchEvent(
          new CustomEvent('cart:updated', { detail: data })
        );

        this.$nextTick(() => {
          const target = this.progressPercent;
          let start = this.giftAnimProgress;

          const step = () => {
            start += (target - start) * 0.15;
            this.giftAnimProgress = start;

            if (Math.abs(target - start) > 0.3) {
              requestAnimationFrame(step);
            } else {
              this.giftAnimProgress = target;
            }
          };

          step();
        });
      } catch (e) {
        console.error('[GA] fetchCart error', e);
      }
    },

    async _changeCart(key, qty) {
      this.updating = true;
      this.updatingKey = key;

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });

        await this.fetchCart();
      } catch (e) {
        console.error('[GA] cart change error', e);
      } finally {
        this.updating = false;
        this.updatingKey = null;
      }
    },

    async incrementItem(item) {
      if (this.updating) return;

      if (this.isFreeGift(item)) {
        this.updating = true;
        this.updatingKey = item.key;

        try {
          if (window.GiftEngine?.addPaidCopyOfGiftVariant) {
            await window.GiftEngine.addPaidCopyOfGiftVariant(item.variant_id);
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.variant_id,
                quantity: 1
              }),
            });
          }

          await this.fetchCart();
        } catch (e) {
          console.error('[GA] increment free gift error', e);
        } finally {
          this.updating = false;
          this.updatingKey = null;
        }

        return;
      }

      this._changeCart(item.key, item.quantity + 1);
    },

    decrementItem(item) {
      if (item.quantity <= 1) {
        this.removeItem(item.key);
        return;
      }

      this._changeCart(item.key, item.quantity - 1);
    },

    updateQuantity(key, q) {
      q < 1 ? this.removeItem(key) : this._changeCart(key, q);
    },

    removeItem(key) {
      this._changeCart(key, 0);
    },

    giftLabel(item) {
      if (item?.properties?._free_gift_m1 === 'true') return 'Free gift (Tier 1)';
      if (item?.properties?._free_gift_m2 === 'true') return 'Free gift (Tier 2)';
      return 'Free gift included';
    },
  }));
});

withAlpine((Alpine) => {
  Alpine.data('gaAddToCart', (cfg = {}) => ({
    selectedVariant: cfg.initialVariantId || null,
    productId: cfg.productId || null,
    variantIds: (Array.isArray(cfg.variantIds) ? cfg.variantIds : []).map(String),
    quantity: 1,
    adding: false,
    showDynamicCheckout: cfg.showDynamicCheckout ?? true,
    showGiftCardRecipient: cfg.showGiftCardRecipient ?? false,

    decrement() {
      if (this.quantity > 1) this.quantity--;
    },

    increment() {
      this.quantity++;
    },

    /* "variant-selected" is dispatched on window, so a form hears the picks made for every other
       product on the page too (PDP selector vs. recommendation / recently-viewed cards). Ignore
       any id that is not one of this product's own variants. */
    ownsVariant(id) {
      if (!this.variantIds.length) return true;
      return this.variantIds.includes(String(id));
    },

    onVariantSelected(id) {
      if (!id || !this.ownsVariant(id)) return;
      this.selectedVariant = id;
    },

    buildPayload(fd) {
      const p = {};
      fd.forEach((v, k) => {
        if (v === '' || v === null) return;
        if (p[k] === undefined) p[k] = v;
        else {
          if (!Array.isArray(p[k])) p[k] = [p[k]];
          p[k].push(v);
        }
      });
      return p;
    },

    async submitForm(evt) {
      if (this.adding) return;
      this.adding = true;

      try {
        const form = evt.target.closest('form');

        /* Last line of defence: never post an id that belongs to another product. */
        if (!this.ownsVariant(this.selectedVariant)) {
          this.selectedVariant = cfg.initialVariantId || null;
        }

        if (form && this.selectedVariant) {
          const idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = this.selectedVariant;
        }

        const payload = this.buildPayload(new FormData(form));

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!addRes.ok) {
          const err = await addRes.json().catch(() => ({}));
          const msg = err.description || err.message || window.cartStrings?.error || window.cartStrings?.could_not_add || 'Could not add to cart';
          alert(msg);
          return;
        }

        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                platform: 'web',
              },
            }),
          });
        } catch (err) {
          console.error('[GA] cart attribute update error', err);
        }

        window.dispatchEvent(new CustomEvent('toggle-cart'));
      } catch (e) {
        console.error('[GA] add-to-cart error', e);
      } finally {
        this.adding = false;
      }
    },
  }));
});